/**
 * LawbillitySyncStatus.jsx — pipeline-accordion LawBillity sync panel.
 *
 * Stage-agnostic, self-fetching status + resync control for whether a
 * matter has landed in LawBillity. LawBillity project creation fires
 * automatically right after the SharePoint folder is created (see
 * hub/sharepoint_utils.py) — long before conflict check / OB / CCL even
 * complete — so this deliberately does NOT live inside any pipeline
 * stage. It mirrors EmailDeliveryAudit's self-fetch shape and sits
 * alongside it in the matter accordion footer.
 *
 * Backend:
 *   GET  /pipeline/matter/{ref}/lawbillity-status
 *   POST /pipeline/matter/{ref}/resync-lawbillity   (DIRECTOR/ADMIN/CLIENT_CARE)
 */
import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "../config/api";
import { Link2, CheckCircle2, AlertTriangle, RefreshCw } from "lucide-react";
import { errorMessage } from "../utils/errorMessage";

const getHeaders = () => {
  const uid =
    sessionStorage.getItem("bonsai_user_id") ||
    localStorage.getItem("bonsai_user_id") ||
    "";
  return uid ? { "X-Bonsai-User-Id": uid } : {};
};

function fmt(ts) {
  if (!ts) return null;
  try {
    const d = new Date(ts);
    return (
      d.toLocaleDateString("en-GB", { day: "2-digit", month: "short" }) +
      " " +
      d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })
    );
  } catch {
    return String(ts).slice(0, 16);
  }
}

export default function LawbillitySyncStatus({ matterReference, isDirector = false }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [resyncError, setResyncError] = useState(null);

  const fetchStatus = useCallback(async () => {
    if (!matterReference) return;
    setLoading(true);
    setError(null);
    try {
      const resp = await axios.get(
        `${API}/pipeline/matter/${encodeURIComponent(matterReference)}/lawbillity-status`,
        { headers: getHeaders() },
      );
      setData(resp.data || null);
    } catch (err) {
      setError(errorMessage(err, "Could not load LawBillity status."));
    } finally {
      setLoading(false);
    }
  }, [matterReference]);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  if (!matterReference) return null;
  // Not a real matter yet (pre-CaseMinder / no submission linked) — nothing to sync.
  if (!loading && data && data.found === false) return null;

  const handleResync = async (e) => {
    e.stopPropagation();
    setBusy(true);
    setResyncError(null);
    try {
      const resp = await axios.post(
        `${API}/pipeline/matter/${encodeURIComponent(matterReference)}/resync-lawbillity`,
        {},
        { headers: getHeaders() },
      );
      if (!resp.data?.ok) {
        setResyncError(
          resp.data?.error ||
            (Array.isArray(resp.data?.errors) ? resp.data.errors.join("; ") : null) ||
            "LawBillity did not confirm success — check the DLQ.",
        );
      }
      await fetchStatus();
    } catch (err) {
      setResyncError(errorMessage(err, "Resync failed — try again"));
    } finally {
      setBusy(false);
    }
  };

  const synced = !!data?.synced;
  const failed = !synced && (data?.dlq_pending || !!data?.error) && !loading;

  return (
    <div
      onClick={(e) => e.stopPropagation()}
      className="bg-navy border border-navy-border rounded p-2 text-xs mt-3"
      data-testid="lawbillity-sync-status"
    >
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Link2 className="w-3.5 h-3.5 text-teal flex-shrink-0" />
          <span className="text-[11px] font-bold text-white">LawBillity</span>

          {loading && !data && (
            <span className="text-[11px] text-txt-muted">Checking…</span>
          )}

          {!loading && synced && (
            <span
              className="inline-flex items-center gap-1 text-[11px] font-semibold text-status-green"
              data-testid="lawbillity-synced-pill"
            >
              <CheckCircle2 className="w-3.5 h-3.5" />
              Synced
              {data.project_id && (
                <span className="text-txt-muted font-normal">· project {data.project_id}</span>
              )}
              {data.synced_at && (
                <span className="text-txt-faint font-normal">{fmt(data.synced_at)}</span>
              )}
            </span>
          )}

          {!loading && !synced && data && (
            <span
              className={`inline-flex items-center gap-1 text-[11px] font-semibold ${
                failed ? "text-status-red" : "text-status-amber"
              }`}
              data-testid="lawbillity-unsynced-pill"
            >
              <AlertTriangle className="w-3.5 h-3.5" />
              {failed ? "Sync failed" : "Not yet synced"}
              {data.last_attempt_at && (
                <span className="text-txt-faint font-normal">
                  last attempt {fmt(data.last_attempt_at)}
                </span>
              )}
            </span>
          )}

          {!loading && error && (
            <span className="text-[11px] text-status-amber">{error}</span>
          )}
        </div>

        {isDirector && !loading && data && !synced && (
          <button
            onClick={handleResync}
            disabled={busy}
            title="Retry creating this matter in LawBillity"
            data-testid="lawbillity-resync-btn"
            className={`inline-flex items-center gap-1 rounded border px-2 py-1 text-[11px] font-semibold transition-colors ${
              busy
                ? "border-navy-border text-txt-muted cursor-not-allowed"
                : "border-teal text-teal hover:bg-teal hover:text-navy cursor-pointer"
            }`}
          >
            <RefreshCw className={`w-3 h-3 ${busy ? "animate-spin" : ""}`} />
            {busy ? "Syncing…" : "Resync to LawBillity"}
          </button>
        )}
      </div>

      {data?.error && !synced && (
        <div className="mt-1 text-[10px] text-status-red" data-testid="lawbillity-error-detail">
          {data.error}
        </div>
      )}
      {resyncError && (
        <div className="mt-1 text-[10px] text-status-red" data-testid="lawbillity-resync-error">
          {resyncError}
        </div>
      )}
    </div>
  );
}
